import { load } from 'js-yaml';

export interface AppSettings {
  featureFlags: {
    weightingTransparency: {
      aboutPage: boolean;
      resultClusterDetails: boolean;
    };
    startPage: {
      whatToExpectCard: boolean;
    };
    preparationStep: {
      enabled: boolean;
      showConfirmationCheckbox: boolean;
    };
    reviewStep: {
      enabled: boolean;
    };
    resultPage: {
      clusterDetailSection: boolean;
      clusterDetailMode: 'inline' | 'modal';
      showPdfExportButton: boolean;
      pdfExportMode: 'browserPrint' | 'reactPdf';
      showClusterBluete: boolean;
      clusterBlueteMode: 'absolute' | 'relative';
    };
    analytics: {
      enabled: boolean;
    };
  };
}

export const DEFAULT_SETTINGS: AppSettings = {
  featureFlags: {
    weightingTransparency: {
      aboutPage: true,
      resultClusterDetails: true,
    },
    startPage: {
      whatToExpectCard: true,
    },
    preparationStep: {
      enabled: true,
      showConfirmationCheckbox: true,
    },
    reviewStep: {
      enabled: true,
    },
    resultPage: {
      clusterDetailSection: true,
      clusterDetailMode: 'inline',
      showPdfExportButton: true,
      pdfExportMode: 'browserPrint',
      showClusterBluete: false,
      clusterBlueteMode: 'absolute',
    },
    analytics: {
      enabled: true,
    },
  },
};

function toBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === 'boolean' ? value : fallback;
}

function toEnum<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === 'string' && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback;
}

function getRecord(value: unknown, key: string): Record<string, unknown> {
  if (!value || typeof value !== 'object') return {};
  const child = (value as Record<string, unknown>)[key];
  return child && typeof child === 'object' ? (child as Record<string, unknown>) : {};
}

/** Lädt `public/config/settings.yaml` und merged sie fehlertolerant über die Defaults. */
export async function loadSettings(): Promise<AppSettings> {
  const res = await fetch('/config/settings.yaml');

  if (!res.ok) {
    throw new Error('Konnte settings.yaml nicht laden');
  }

  const text = await res.text();
  const data = load(text);

  if (!data || typeof data !== 'object' || Array.isArray(data)) {
    throw new Error('Ungültige YAML-Struktur in settings.yaml');
  }

  const flags = getRecord(data, 'featureFlags');
  const weighting = getRecord(flags, 'weightingTransparency');
  const startPage = getRecord(flags, 'startPage');
  const preparationStep = getRecord(flags, 'preparationStep');
  const reviewStep = getRecord(flags, 'reviewStep');
  const resultPage = getRecord(flags, 'resultPage');
  const analytics = getRecord(flags, 'analytics');

  return {
    featureFlags: {
      weightingTransparency: {
        aboutPage: toBoolean(
          weighting.aboutPage,
          DEFAULT_SETTINGS.featureFlags.weightingTransparency.aboutPage,
        ),
        resultClusterDetails: toBoolean(
          weighting.resultClusterDetails,
          DEFAULT_SETTINGS.featureFlags.weightingTransparency.resultClusterDetails,
        ),
      },
      startPage: {
        whatToExpectCard: toBoolean(
          startPage.whatToExpectCard,
          DEFAULT_SETTINGS.featureFlags.startPage.whatToExpectCard,
        ),
      },
      preparationStep: {
        enabled: toBoolean(
          preparationStep.enabled,
          DEFAULT_SETTINGS.featureFlags.preparationStep.enabled,
        ),
        showConfirmationCheckbox: toBoolean(
          preparationStep.showConfirmationCheckbox,
          DEFAULT_SETTINGS.featureFlags.preparationStep.showConfirmationCheckbox,
        ),
      },
      reviewStep: {
        enabled: toBoolean(reviewStep.enabled, DEFAULT_SETTINGS.featureFlags.reviewStep.enabled),
      },
      resultPage: {
        clusterDetailSection: toBoolean(
          resultPage.clusterDetailSection,
          DEFAULT_SETTINGS.featureFlags.resultPage.clusterDetailSection,
        ),
        clusterDetailMode: toEnum(
          resultPage.clusterDetailMode,
          ['inline', 'modal'],
          DEFAULT_SETTINGS.featureFlags.resultPage.clusterDetailMode,
        ),
        showPdfExportButton: toBoolean(
          resultPage.showPdfExportButton,
          DEFAULT_SETTINGS.featureFlags.resultPage.showPdfExportButton,
        ),
        pdfExportMode: toEnum(
          resultPage.pdfExportMode,
          ['browserPrint', 'reactPdf'],
          DEFAULT_SETTINGS.featureFlags.resultPage.pdfExportMode,
        ),
        showClusterBluete: toBoolean(
          resultPage.showClusterBluete,
          DEFAULT_SETTINGS.featureFlags.resultPage.showClusterBluete,
        ),
        clusterBlueteMode: toEnum(
          resultPage.clusterBlueteMode,
          ['absolute', 'relative'],
          DEFAULT_SETTINGS.featureFlags.resultPage.clusterBlueteMode,
        ),
      },
      analytics: {
        enabled: toBoolean(analytics.enabled, DEFAULT_SETTINGS.featureFlags.analytics.enabled),
      },
    },
  };
}
